/**
 * Server-side validation of a field definition and a purpose binding.
 *
 * API rule 1 of Section 6 says: read app.field for the record, and reject an
 * unknown field id. This file is the other half of that promise. It rejects a
 * field whose component id is not real, whose component cannot read the field's
 * value type, or whose props the component never declared.
 *
 * Postgres cannot make these checks. No component id reaches the database, on
 * purpose, so app.field.component is `not null` and nothing more. The server is
 * the gate. Call this on every template import and every field patch.
 */
import { COMPONENTS } from '../generated/components';
import type { PropDecl } from '../generated/components';
import { isComponentId } from '../generated/component-ids';
import { PURPOSES, isPurposeId } from '../generated/purposes';
import { VALUE_TYPES, isValueTypeId } from '../generated/value-types';
import type { ValueTypeHome, ValueTypeId } from '../generated/value-types';

export type RegistryProblem = {
  /** Stable, machine readable. Safe to branch on. */
  readonly code: string;
  /** Plain English, safe to show a user. Names the field or the prop. */
  readonly message: string;
  /** Which prop the problem is about, when it is about one. */
  readonly prop?: string;
};

export type FieldDefinitionInput = {
  /** app.field.type */
  readonly type: string;
  /** app.field.component */
  readonly component: string;
  /** app.field.props */
  readonly props?: Readonly<Record<string, unknown>> | null;
  /** app.field.storage. Widened to four values by migration 0007, five by 0036. */
  readonly storage?: FieldStorage | null;
  /** app.field.slot, such as n2 or r1 */
  readonly slot?: string | null;
  /** app.field.attr_key */
  readonly attr_key?: string | null;
  /** app.field.required */
  readonly required?: boolean;
  /** app.field.target_record */
  readonly target_record?: string | null;
};

/**
 * The five values of app.field_storage: migration 0007 widened it from two,
 * and 0036 added the fifth. 'link' means data.entry_link owns the value.
 * 'computed' means nothing stores it. 'children' means the row stores nothing
 * and the value is the entries of a lines record.
 */
export const FIELD_STORAGE_VALUES = ['slot', 'attr', 'link', 'computed', 'children'] as const;

export type FieldStorage = (typeof FIELD_STORAGE_VALUES)[number];

/** Which storage each home demands. Section 5.2, and the constraint in 0007. */
const STORAGE_FOR_HOME: Record<ValueTypeHome, FieldStorage> = {
  numeric_slot: 'slot',
  text_slot: 'slot',
  time_slot: 'slot',
  reference_slot: 'slot',
  boolean_slot: 'slot',
  attrs: 'attr',
  link_table: 'link',
  computed: 'computed',
  child_record: 'children',
};

/** How to say a home out loud, for an error message a person reads. */
const WHERE: Record<ValueTypeHome, string> = {
  numeric_slot: 'a numeric slot',
  text_slot: 'a text slot',
  time_slot: 'a time slot',
  reference_slot: 'a reference slot',
  boolean_slot: 'a boolean slot',
  attrs: 'the attrs document',
  link_table: 'data.entry_link, because it holds a set',
  computed: 'nowhere, because it is computed',
  child_record: 'a lines record, because each line is an entry of its own',
};

/** Which slot letter each home demands. Section 5.2. */
const SLOT_LETTER: Partial<Record<ValueTypeHome, string>> = {
  numeric_slot: 'n',
  text_slot: 't',
  time_slot: 'd',
  reference_slot: 'r',
  boolean_slot: 'b',
};

function checkProp(key: string, decl: PropDecl, value: unknown): RegistryProblem | null {
  const bad = (message: string): RegistryProblem => ({ code: 'PROP_TYPE', message, prop: key });
  switch (decl.type) {
    case 'string':
    case 'field_id':
    case 'record_id':
      return typeof value === 'string' ? null : bad(`Prop "${key}" must be a string.`);
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? null
        : bad(`Prop "${key}" must be a finite number.`);
    case 'boolean':
      return typeof value === 'boolean' ? null : bad(`Prop "${key}" must be true or false.`);
    case 'string[]':
      return Array.isArray(value) && value.every((v) => typeof v === 'string')
        ? null
        : bad(`Prop "${key}" must be a list of strings.`);
    case 'enum': {
      const allowed = decl.values ?? [];
      if (typeof value === 'string' && allowed.includes(value)) return null;
      return {
        code: 'PROP_ENUM',
        message: `Prop "${key}" must be one of ${allowed.join(', ')}.`,
        prop: key,
      };
    }
  }
}

/**
 * Every problem with one field definition. An empty array means the field is
 * valid against this registry build.
 */
export function validateFieldDefinition(input: FieldDefinitionInput): RegistryProblem[] {
  const problems: RegistryProblem[] = [];

  if (!isValueTypeId(input.type)) {
    return [{
      code: 'VALUE_TYPE_UNKNOWN',
      message: `Field type "${input.type}" is not one of the 17 value types.`,
    }];
  }
  const valueType = VALUE_TYPES[input.type];

  if (!isComponentId(input.component)) {
    problems.push({
      code: 'COMPONENT_UNKNOWN',
      message: `Component "${input.component}" is not in the registry. The renderer would fall back to "${valueType.primitive}".`,
    });
  } else {
    const entry = COMPONENTS[input.component];
    if (!entry.value_types.includes(valueType.id)) {
      problems.push({
        code: 'COMPONENT_TYPE_MISMATCH',
        message: `Component "${entry.id}" reads ${entry.value_types.join(', ')} and cannot draw a "${valueType.id}" field.`,
      });
    }
    for (const [key, value] of Object.entries(input.props ?? {})) {
      const decl = entry.props[key];
      if (!decl) {
        problems.push({
          code: 'PROP_UNKNOWN',
          message: `Component "${entry.id}" declares no prop "${key}".`,
          prop: key,
        });
        continue;
      }
      if (value === null || value === undefined) continue; // every prop is optional
      const problem = checkProp(key, decl, value);
      if (problem) problems.push(problem);
    }
  }

  if (valueType.id === 'reference' && !input.target_record) {
    problems.push({
      code: 'REFERENCE_NEEDS_TARGET',
      message: 'A reference field must name the directory it points at.',
    });
  }

  problems.push(...checkStorage(input, valueType.home, valueType.id));
  return problems;
}

function checkStorage(
  input: FieldDefinitionInput,
  home: ValueTypeHome,
  valueType: ValueTypeId,
): RegistryProblem[] {
  const problems: RegistryProblem[] = [];
  if (!input.storage) return problems; // the caller is validating a draft

  const wanted = STORAGE_FOR_HOME[home];

  /**
   * The cold tail, Section 5.2 of the architecture: "anything with no slot
   * free" lands in the attrs document, keyed by attr_key. So a slot-home type
   * has two legal homes, not one, and the structure editor needs the second
   * one the day a record fills a family.
   *
   * Two types never take it. A `reference` and a `select` hold a foreign key,
   * and a value inside a document cannot carry one, so a dangling id would
   * become possible. Constraint field_relation_never_in_attrs says the same
   * thing in the database.
   */
  const overflow =
    wanted === 'slot' && input.storage === 'attr' && home !== 'reference_slot';

  /**
   * A `formula` is the one type whose home the registry cannot name.
   *
   * The registry gives each value type ONE home, and a formula has three: its
   * answer is a number, a piece of text, or a yes-or-no, and only its own
   * recipe says which. `app.field.spec` carries that recipe and the registry
   * does not read it, so this check hands the decision to the planner, which
   * does. Migration 0011 holds the same rule in the database: a formula takes
   * a slot, and `slotFamilyFor` in formula.ts picks the family.
   *
   * `rollup` and `lookup` are NOT here. Both read beyond this row, so neither
   * has an answer to keep, and both stay `computed`.
   */
  const formulaSlot = valueType === 'formula' && input.storage === 'slot';

  if (input.storage !== wanted && !overflow && !formulaSlot) {
    const reason =
      wanted === 'slot' && input.storage === 'attr'
        ? `A "${valueType}" field holds a real key, so it must take ${WHERE[home]} and never the attrs document.`
        : `A "${valueType}" field lives in ${WHERE[home]}, so storage must be ${wanted}, not ${input.storage}.`;
    problems.push({ code: 'STORAGE_MISMATCH', message: reason });
    return problems; // every check below assumes the storage is right
  }

  // An overflow field is an attrs field in every way that matters: it carries
  // a key, it holds no slot, and it cannot be required.
  if (overflow) {
    if (input.slot) {
      problems.push({
        code: 'STORAGE_TARGET',
        message: 'A field in the attrs document must leave slot empty.',
      });
    }
    if (!input.attr_key) {
      problems.push({
        code: 'STORAGE_TARGET',
        message: 'A field in the attrs document needs an attr_key.',
      });
    }
    if (input.required) {
      problems.push({
        code: 'REQUIRED_IN_ATTRS',
        message: 'A required field must not live in attrs. attrs cannot hold not null, and two write paths exist. Promote it to a slot.',
      });
    }
    return problems;
  }

  // Storage rule 1 of Section 5.2, a relation never lives in attrs, needs no
  // check of its own any more. Every relation type has a slot or a link home in
  // value-types.json, so the mismatch above already refuses attrs, and with a
  // better message: it names the storage to use instead.
  // Migration 0007, constraint field_storage_target. Two values want a target
  // column, and two want neither.
  if (wanted === 'slot' || formulaSlot) {
    // The family letter comes from the home, and a formula has no fixed home:
    // its own result names the family, and `slotFamilyFor` in formula.ts is
    // what reads it. So the family is the planner's check, not this one, and
    // only the two rules that hold for every slot field are applied here.
    const letter = formulaSlot ? '' : (SLOT_LETTER[home] ?? '');
    if (letter !== '' && input.slot && !input.slot.startsWith(letter)) {
      problems.push({
        code: 'SLOT_FAMILY',
        message: `A "${valueType}" field takes a ${letter} slot, not "${input.slot}".`,
      });
    }
    if (!input.slot) {
      problems.push({
        code: 'STORAGE_TARGET',
        message: 'A field in a slot needs a slot.',
      });
    }
    if (input.attr_key) {
      problems.push({
        code: 'STORAGE_TARGET',
        message: 'A slot field must leave attr_key empty.',
      });
    }
  } else if (wanted === 'attr') {
    if (input.slot) {
      problems.push({
        code: 'STORAGE_TARGET',
        message: 'An attr field must leave slot empty.',
      });
    }
    // Section 5.2, storage rule 2. Also constraint field_required_never_in_attrs.
    if (input.required) {
      problems.push({
        code: 'REQUIRED_IN_ATTRS',
        message: 'A required field must not live in attrs. attrs cannot hold not null, and two write paths exist. Promote it to a slot.',
      });
    }
  } else if (input.slot || input.attr_key) {
    problems.push({
      code: 'STORAGE_TARGET',
      message: `A "${wanted}" field must leave both slot and attr_key empty.`,
    });
  }

  return problems;
}

export type BindingInput = {
  /** app.record_binding.purpose */
  readonly purpose: string;
  /** app.field.type of the field named, or null when const_num answers. */
  readonly field_type?: string | null;
  /** app.record_binding.const_num */
  readonly const_num?: number | null;
};

/** Every problem with one purpose binding. */
export function validateBinding(input: BindingInput): RegistryProblem[] {
  if (!isPurposeId(input.purpose)) {
    return [{
      code: 'PURPOSE_UNKNOWN',
      message: `Purpose "${input.purpose}" is not one of the six engine questions.`,
    }];
  }
  const purpose = PURPOSES[input.purpose];
  const problems: RegistryProblem[] = [];
  const hasConst = input.const_num !== null && input.const_num !== undefined;

  if (hasConst) {
    if (!purpose.const_allowed) {
      problems.push({
        code: 'CONST_NOT_ALLOWED',
        message: `Purpose "${purpose.id}" must be answered by a field, not by a constant.`,
      });
    } else if (input.const_num !== 1 && input.const_num !== -1) {
      problems.push({
        code: 'CONST_OUT_OF_RANGE',
        message: 'A sign constant must be 1 or -1.',
      });
    }
    if (input.field_type) {
      problems.push({
        code: 'BINDING_AMBIGUOUS',
        message: `Purpose "${purpose.id}" names both a field and a constant. Pick one.`,
      });
    }
    return problems;
  }

  if (!input.field_type) {
    problems.push({
      code: 'BINDING_EMPTY',
      message: `Purpose "${purpose.id}" names neither a field nor a constant.`,
    });
    return problems;
  }
  if (!isValueTypeId(input.field_type)) {
    problems.push({
      code: 'VALUE_TYPE_UNKNOWN',
      message: `Field type "${input.field_type}" is not one of the 17 value types.`,
    });
    return problems;
  }
  if (!purpose.answered_by.includes(input.field_type)) {
    problems.push({
      code: 'PURPOSE_TYPE_MISMATCH',
      message: `Purpose "${purpose.id}" drives ${purpose.what_it_drives}, and a "${input.field_type}" field cannot answer it. Allowed: ${purpose.answered_by.join(', ')}.`,
    });
  }
  return problems;
}
